import { useMemo, useState, Suspense, useCallback } from 'react'
import { Canvas } from '@react-three/fiber'
import { EffectComposer, Bloom, ChromaticAberration, Vignette, Noise } from '@react-three/postprocessing'
import { BlendFunction } from 'postprocessing'
import Scene from './components/Scene'
import Navigation from './components/UI/Navigation'
import AudioController from './components/AudioController'
import LoadingScreen from './components/LoadingScreen'
import ErrorBoundary from './components/ErrorBoundary'
import { useMobile } from './hooks/useMobile'
import './App.css'

/**
 * Main App component with responsive canvas and mobile-optimized UI
 */
function App() {
  const { isMobileOrTablet, isMobile } = useMobile()
  const [isLoading, setIsLoading] = useState(true)

  // Handle canvas creation - dismiss loading screen after short delay
  const handleCanvasCreated = useCallback(() => {
    // Small delay to ensure first frame is rendered
    setTimeout(() => {
      setIsLoading(false)
    }, 500)
  }, [])

  // Optimize rendering settings for mobile devices
  const canvasProps = useMemo(() => ({
    camera: {
      // Y=3 provides good viewing angle with OrbitControls polar constraints
      // Z=25 is the overview distance, CameraController will animate from here
      position: [0, 3, 25],
      fov: isMobile ? 85 : 75 // Wider FOV on mobile for better viewing
    },
    gl: {
      antialias: !isMobileOrTablet, // Disable antialiasing on mobile for performance
      alpha: false,
      powerPreference: isMobileOrTablet ? 'default' : 'high-performance',
      // Limit pixel ratio on mobile for performance
      ...(isMobileOrTablet && { pixelRatio: Math.min(window.devicePixelRatio, 2) })
    },
    // Lower DPR on mobile devices for better performance
    dpr: isMobileOrTablet ? [1, 1.5] : [1, 2]
  }), [isMobileOrTablet, isMobile])

  // Reduce post-processing effects on mobile for performance
  const effectSettings = useMemo(() => ({
    bloom: {
      intensity: isMobileOrTablet ? 0.3 : 0.5,
      luminanceThreshold: isMobileOrTablet ? 0.5 : 0.4,
      luminanceSmoothing: 0.9,
      height: isMobileOrTablet ? 200 : 300,
      opacity: isMobileOrTablet ? 0.6 : 0.8
    },
    chromaticAberration: {
      enabled: !isMobile, // Disable on small mobile for performance
      offset: [0.0005, 0.0005]
    },
    vignette: {
      offset: 0.3,
      darkness: isMobileOrTablet ? 0.4 : 0.5
    },
    noise: {
      enabled: !isMobile, // Disable on small mobile for performance
      opacity: 0.03
    },
    multisampling: isMobileOrTablet ? 4 : 8
  }), [isMobileOrTablet, isMobile])

  return (
    <ErrorBoundary>
    <div className="app-container">
      {/* Loading Screen */}
      <LoadingScreen isLoading={isLoading} />

      {/* Navigation - renders differently on mobile */}
      <Navigation />

      {/* Audio Controller - renders differently on mobile */}
      <AudioController />

      {/* 3D Canvas - takes full viewport */}
      <Canvas
        className="main-canvas"
        camera={canvasProps.camera}
        gl={canvasProps.gl}
        dpr={canvasProps.dpr}
        // Optimize event handling for touch
        eventSource={document.getElementById('root')}
        eventPrefix="client"
        onCreated={handleCanvasCreated}
      >
        <color attach="background" args={['#0a0a1a']} />

        <Suspense fallback={null}>
          <Scene />
        </Suspense>

        <EffectComposer multisampling={effectSettings.multisampling}>
          {/* Golden bloom for magical glow */}
          <Bloom
            intensity={effectSettings.bloom.intensity}
            luminanceThreshold={effectSettings.bloom.luminanceThreshold}
            luminanceSmoothing={effectSettings.bloom.luminanceSmoothing}
            height={effectSettings.bloom.height}
            opacity={effectSettings.bloom.opacity}
          />

          {/* Subtle chromatic aberration for depth - disabled on small mobile */}
          {effectSettings.chromaticAberration.enabled && (
            <ChromaticAberration
              blendFunction={BlendFunction.NORMAL}
              offset={effectSettings.chromaticAberration.offset}
            />
          )}

          {/* Vignette for focus */}
          <Vignette
            offset={effectSettings.vignette.offset}
            darkness={effectSettings.vignette.darkness}
            eskil={false}
            blendFunction={BlendFunction.NORMAL}
          />

          {/* Film grain for texture - disabled on small mobile */}
          {effectSettings.noise.enabled && (
            <Noise
              opacity={effectSettings.noise.opacity}
              blendFunction={BlendFunction.OVERLAY}
            />
          )}
        </EffectComposer>
      </Canvas>
    </div>
    </ErrorBoundary>
  )
}

export default App
